import { useCallback } from 'react';
import {
  useCardToHaveListQuantityUpdateMutation,
  useRemoveCardFromHaveListMutation,
} from 'store/services/collectionList/haveList/haveListApi';
import { OfferLineItem } from 'types/interfaces/tradeOffer';
import { useOfferBuilder, OfferSide } from '../context/OfferBuilderContext';

/**
 * THE way a line leaves a side of the offer, shared by the cart's × and the
 * focus grid's "✓ Added" (Asana 1219902279124885 Decision 4), so the two
 * controls cannot disagree about what removal means.
 *
 * Removes the line, undoing the have row if THIS session's un-catalogued add
 * created it. Without the undo a sender browsing want lists would silently
 * accumulate a collection of cards they never meant to list.
 *
 * The safety rule is asymmetric on purpose:
 *   - row created by this add   → delete it (nothing existed before)
 *   - row that already existed  → DECREMENT ONLY, never delete
 *
 * The second case is the one that can destroy real user data: the sender
 * catalogued those copies themselves, and deleting the row would take the
 * whole stack, not just the copy this offer borrowed.
 *
 * The line is removed from the offer regardless of whether the undo call
 * succeeds — leaving a card in the offer because a cleanup request failed
 * would be a worse outcome than a stranded row the sender can still see and
 * edit on their have list.
 */
export const useRemoveOfferLine = (side: OfferSide) => {
  const { removeItem, getBuilderCreatedRow, forgetBuilderCreatedRow } = useOfferBuilder();
  const [removeCardFromHaveList] = useRemoveCardFromHaveListMutation();
  const [adjustHaveListQuantity] = useCardToHaveListQuantityUpdateMutation();

  return useCallback(
    async (item: Pick<OfferLineItem, 'userMTGCardHaveId'>) => {
      const created = getBuilderCreatedRow(item.userMTGCardHaveId);
      removeItem(side, item.userMTGCardHaveId);
      if (!created) return;

      try {
        if (created.wasCreated) {
          await removeCardFromHaveList({ haveListRecordId: item.userMTGCardHaveId }).unwrap();
        } else {
          await adjustHaveListQuantity({
            haveListRecordId: item.userMTGCardHaveId,
            change: -created.addedQuantity,
          }).unwrap();
        }
        forgetBuilderCreatedRow(item.userMTGCardHaveId);
      } catch {
        // Keep the mark: the row still reflects this session's add, so a later
        // removal (or a retry) can still undo it rather than losing track.
      }
    },
    [
      side,
      removeItem,
      getBuilderCreatedRow,
      forgetBuilderCreatedRow,
      removeCardFromHaveList,
      adjustHaveListQuantity,
    ]
  );
};

export default useRemoveOfferLine;
